import { useMemo } from 'react'
import {
  getFulfillmentDetails,
  getFulfillmentToggleData
} from 'shop/components/Shop/Fulfillment/utils'
import {
  DELIVERY_FULFILLMENT,
  FulfillmentType,
  ORDER_AT_TABLE_FULFILLMENT,
  PICKUP_FULFILLMENT
} from 'shop/types'
import { getFulfillmentFlags } from 'shop/utils'
import {
  canAcceptOrders,
  findDefaultFulfillment,
  isAnyStoreAcceptingOrders
} from 'shop/utils/common'
import { useCartMatch } from '../useCartMatch'
import { useCheckout } from '../useCheckout'
import { useConsumerCart } from '../useConsumerCart'
import { useShop } from '../useGlobalContext'
import { useQueryParams } from '../useQueryParams'

export const useFulfillment = () => {
  const { partner, currentPartnerStore } = useShop()
  const { cart } = useConsumerCart()
  const { isCartMatch } = useCartMatch()
  const { getQueryParam } = useQueryParams()
  const { toggleFulfillment } = useCheckout()

  const queryParamFulfillmentType =
    (getQueryParam('fulfillment_type').toUpperCase() as FulfillmentType) ||
    undefined
  const cartFulfillment = cart?.fulfillment || null
  const { isOrderAtTable } = getFulfillmentFlags(cartFulfillment?.type)
  const isAnyStoreAvailable = isAnyStoreAcceptingOrders(partner?.stores)
  const defaultFulfillmentInfo =
    findDefaultFulfillment(
      currentPartnerStore?.orderFulfillmentTypes,
      queryParamFulfillmentType
    ) ?? null
  const currentFulfillmentType =
    cartFulfillment?.type || defaultFulfillmentInfo?.fulfillmentType
  const { fulfillmentWindow, orderType, isOat } = getFulfillmentDetails(
    cartFulfillment,
    defaultFulfillmentInfo,
    isCartMatch
  )

  const isStoreAcceptingOrders = useMemo(
    () => canAcceptOrders(currentPartnerStore, queryParamFulfillmentType),
    [currentPartnerStore, queryParamFulfillmentType]
  )

  // Get fulfillment data using helper function
  const pickupData = getFulfillmentToggleData(
    toggleFulfillment,
    PICKUP_FULFILLMENT
  )
  const deliveryData = getFulfillmentToggleData(
    toggleFulfillment,
    DELIVERY_FULFILLMENT
  )
  const orderAtTableData = getFulfillmentToggleData(
    toggleFulfillment,
    ORDER_AT_TABLE_FULFILLMENT
  )

  // Extract minimum order values
  const pickupMinValue = pickupData.minValue
  const deliveryMinValue = deliveryData.minValue
  const orderAtTableMinValue = orderAtTableData.minValue

  // Checks if user can edit their fulfillment details (not limited to fulfillment type)
  const fulfillmentModalEnabled = cartFulfillment
    ? !isOrderAtTable && isAnyStoreAvailable
    : isAnyStoreAvailable

  const isPickupAvailable =
    toggleFulfillment &&
    !!findDefaultFulfillment(toggleFulfillment, PICKUP_FULFILLMENT)

  const isDeliveryAvailable =
    toggleFulfillment &&
    !!findDefaultFulfillment(toggleFulfillment, DELIVERY_FULFILLMENT)

  // Get array of available fulfillment types
  const availableFulfillmentTypes = [
    ...(isPickupAvailable ? [PICKUP_FULFILLMENT] : []),
    ...(isDeliveryAvailable ? [DELIVERY_FULFILLMENT] : [])
  ]

  return {
    fulfillmentModalEnabled,
    currentFulfillmentType,
    fulfillmentWindow,
    orderType,
    isOat,
    pickupMinValue,
    deliveryMinValue,
    orderAtTableMinValue,
    availableFulfillmentTypes,
    isStoreAcceptingOrders
  }
}

export default useFulfillment
